import { kebabCase } from 'lodash';
import React from 'react';
import { Image as ImageType } from '../../../commonTypes/image';
import { TextLink } from '../../../commonTypes/links';
import colors from '../../../helpers/colors';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { generateKeyForElement } from '../../../helpers/elementKeyHelper';
import { applyComponentTheme } from '../../../theme';
import { GridItemProps } from '../../Grid';
import {
  LinkItem,
  LinkItemIcon,
  LinkText,
  SectionBody,
  SectionGridItem,
  SectionHeading,
  SocialMediaLogo,
  StyledLink,
  UnorderedList,
} from './styles';

export type SectionType = {
  title?: string;
  customGridItemProps?: GridItemProps;
};

export type SimpleSectionType = SectionType & {
  links: TextLink[];
};

export type IconSectionType = SectionType & {
  linksWithImages: { image: ImageType; link: TextLink }[];
};

export type RichTextSectionType = SectionType & {
  body: string;
};

export type CustomisableFooterSection =
  | SimpleSectionType
  | IconSectionType
  | RichTextSectionType;

export type SectionProps = {
  section: CustomisableFooterSection;
  gridItemProps: GridItemProps;
  onLinkClick?: (label: string) => void;
} & ComponentProps;

const isSimpleSection = (
  section: CustomisableFooterSection
): section is SimpleSectionType => {
  return (section as SimpleSectionType).links !== undefined;
};

const isIconSection = (
  section: CustomisableFooterSection
): section is IconSectionType => {
  return (section as IconSectionType).linksWithImages !== undefined;
};

const isRichtextSection = (
  section: CustomisableFooterSection
): section is RichTextSectionType => {
  return (section as RichTextSectionType).body !== undefined;
};

const Section: React.FC<SectionProps> = ({
  section,
  gridItemProps,
  onLinkClick,
  ...props
}) => {
  const ariaLabelledby = section.title ? kebabCase(section.title) : undefined;
  return (
    <SectionGridItem
      isIconSection={isIconSection(section)}
      {...gridItemProps}
      {...componentProps(props)}>
      {section.title && (
        <SectionHeading isIconSection={isIconSection(section)} id={ariaLabelledby}>
          {section.title}
        </SectionHeading>
      )}
      {isSimpleSection(section) && (
        <UnorderedList aria-labelledby={ariaLabelledby}>
          {section.links.map((link) => (
            <LinkItem key={generateKeyForElement(link)}>
              <StyledLink
                href={link.url}
                onClick={(e) => {
                  if (onLinkClick) {
                    onLinkClick(link.text);
                  }
                  if (link.onClick) {
                    link.onClick(e);
                  }
                }}
                color={colors.neutral02}
                removeVisitedStyle
                openInNewTab={link.openInNewTab}>
                {link.text}
                {link.openInNewTab && (
                  <LinkItemIcon name="new-tab" size="small" color={colors.core01} />
                )}
              </StyledLink>
            </LinkItem>
          ))}
        </UnorderedList>
      )}
      {isIconSection(section) && (
        <UnorderedList isIconSection aria-labelledby={ariaLabelledby}>
          {section.linksWithImages.map((linkSection) => (
            <LinkItem key={generateKeyForElement(linkSection)}>
              <StyledLink
                href={linkSection.link.url}
                onClick={linkSection.link.onClick}
                color={colors.neutral02}
                removeVisitedStyle
                openInNewTab={linkSection.link.openInNewTab}>
                <SocialMediaLogo image={linkSection.image} focusable={false} />
                <LinkText>{linkSection.link.text}</LinkText>
              </StyledLink>
            </LinkItem>
          ))}
        </UnorderedList>
      )}
      {isRichtextSection(section) && (
        <div aria-labelledby={ariaLabelledby}>
          <SectionBody focusable html={section.body} />
        </div>
      )}
    </SectionGridItem>
  );
};

export default applyComponentTheme(Section, 'Section');
